﻿import $ from 'jquery';
import { ScrollTo } from '../utilities/scrollto';

export class DualListBox {

  public static get wireup(): string { return 'dual-listbox'.asDataSelector(); }

  $element: JQuery;
  $searchResults: JQuery;
  $selectedResults: JQuery;
  $selectedValuesCsv: JQuery;

  $searchCount: JQuery;
  $selectedCount: JQuery;

  selectedItemHiddenInputNameValue: string;
  hasGroupItems: boolean;

  constructor($element: JQuery, options: any) {
    this.$element = $element;
    this.$searchResults = $element.find('[data-search-results]');
    this.$selectedResults = $element.find('[data-selected-results]');
    this.$selectedValuesCsv = $element.find('[data-selected-values-csv]');

    this.$searchCount = $('[data-search-results-count]');
    this.$selectedCount = $('[data-selected-count]');

    this.selectedItemHiddenInputNameValue = $element.data('dlb-expression-path') || 'values';
    this.hasGroupItems = $element.data('has-group-items') || false;

    this.$element.on('refresh-selections.duallistbox', this.refreshSelections.bind(this));
    this.$element.on('click.duallistbox', '[data-select-button]', this.onSelectRow.bind(this));
    this.$element.on('click.duallistbox', '[data-remove-button]', this.onRemoveRow.bind(this));
    this.$element.on('click.duallistbox', '[data-select-all]', this.onSelectAll.bind(this));
    this.$element.on('click.duallistbox', '[data-remove-all]', this.onRemoveAll.bind(this));

    this.refreshSelections();
  }

  onSelectAll(e: Event): void {
    let $rows = this.$searchResults.find('[data-row]:not(.is-selected, [data-is-group-item])');
    let self = this;
    $rows.each(function (i) { self.selectRow($(this)); });

    this.onValueChanged();
  }

  onSelectRow(e: Event): void {
    let $row = $(e.target).closest('[data-row]');

    let $selectedRow = this.selectRow($row);

    ScrollTo.execute($selectedRow);
    this.onValueChanged();
  }

  onRemoveAll(e: Event): void {
    let $rows = this.$selectedResults.find('[data-row]:not([data-is-group-item])');
    let self = this;
    $rows.each(function (i) { self.removeRow($(this)); });

    this.onValueChanged();
  }

  onRemoveRow(e: Event): void {
    let $row = <any>$(e.target).closest('[data-row]');
    this.removeRow($row);

    this.onValueChanged();
  }

  selectRow($row: JQuery<EventTarget>): JQuery<HTMLElement> {
    $row.addClass('is-selected');

    let value = $row.data('value');
    if (this.isValueSelected(value))
      return;

    let $selectedRow = this.findAndCloneResultRow(value);

    this.insertRowIntoSelectedItems($selectedRow);

    if (this.hasGroupItems)
      this.handleGroupItemSelectionIfNeeded($selectedRow);

    return $selectedRow;
  }

  handleGroupItemSelectionIfNeeded($selectedRow: JQuery): void {
    let group = $selectedRow.data('group');

    let $searchResultsGrouping = this.findSearchResultsGrouping(group);
    let $searchResultsGroupItem = $searchResultsGrouping.filter('[data-is-group-item]');
    let $selectedResultsGrouping = this.findSelectedResultsGrouping(group);
    let $selectedResultsGroupItem = $selectedResultsGrouping.filter('[data-is-group-item]');

    if ($selectedResultsGroupItem.length == 0) {
      $selectedResultsGroupItem = $searchResultsGroupItem.clone();
      this.insertRowIntoSelectedItems($selectedResultsGroupItem);
    }

    // check to see if there are any additional items left to select in the group, select the group item if user has selected them all
    let $remainingUnselectedItemsForGroup = $searchResultsGrouping.filter('[data-value]:not(.is-selected)');

    if ($remainingUnselectedItemsForGroup.length == 0)
      $searchResultsGroupItem.addClass('is-selected');
  }

  removeRow($row: JQuery): void {
    let value = $row.find('[value]').val() as string;
    $row.remove();
    this.$searchResults.find('value'.asDataSelector(value || "")).removeClass('is-selected');

    if (this.hasGroupItems)
      this.handleGroupItemRemovalIfNeeded($row);
  }

  handleGroupItemRemovalIfNeeded($removedRow: JQuery): void {
    let group = $removedRow.data('group');

    let $searchResultsGrouping = this.findSearchResultsGrouping(group);
    let $searchResultsGroupItem = $searchResultsGrouping.filter('[data-is-group-item]');
    let $selectedResultsGrouping = this.findSelectedResultsGrouping(group);
    let $selectedResultsGroupItem = $selectedResultsGrouping.filter('[data-is-group-item]');

    // unselect search result grouping no matter what
    $searchResultsGroupItem.removeClass('is-selected');

    //remove the selected result group item if no selected items exist.
    let $remainingSelectedItemsForGroup = $selectedResultsGrouping.filter('[data-value]');
    if ($remainingSelectedItemsForGroup.length == 0)
      $selectedResultsGroupItem.remove();
  }

  refreshSelections(): void {
    let $searchResultRows = this.$searchResults.find('[data-row]');
    let self = this;
    $searchResultRows.each(function () {
      let $row = $(this);
      let rowIsSelected = self.isValueSelected($row.data('value'));
      $row.toggleClass('is-selected', rowIsSelected);
    });

    if (this.hasGroupItems) {
      let $groupItems = $searchResultRows.filter('[data-is-group-item]');
      $groupItems.each(function () {
        let $groupItem = $(this);
        let group = $groupItem.data('group');
        let $itemsForGroup = $searchResultRows.filter(`[data-group="${group}"][data-value]`);

        let $unselectedItems = $itemsForGroup.filter(':not(.is-selected)');
        if ($unselectedItems.length == 0)
          $groupItem.addClass('is-selected');
      });
    }
  }

  isValueSelected(value: any): boolean {
    if (value) {
      return this.$selectedResults.find('value'.asAttrSelector(value)).length != 0;
    }
    return this.$selectedResults.find('[name="' + this.selectedItemHiddenInputNameValue + '"]:not([value])').length != 0;
  }

  onValueChanged(): void {
    let $selectedValueInputs = this.$selectedResults.find('[name="' + this.selectedItemHiddenInputNameValue + '"][value!=""]');
    let selectedValuesCsv = $selectedValueInputs.map((i, el) => $(el).val())
      .toArray()
      .join(',');
    this.$selectedValuesCsv.val(selectedValuesCsv);

    this.$element.trigger('change.rustig');
  }

  findAndCloneResultRow(value: any): JQuery {
    let $selectedRow = this.findSearchResultRow(value)
      .clone()
      .removeClass('is-selected');

    $selectedRow.find('[data-select-button]')
      .removeAttr('data-select-button')
      .attr('data-remove-button', 'on');

    $selectedRow.find('[data-icon]')
      .removeClass('icon-arrow-right')
      .addClass('icon-close');

    let $input = $('<input>').attr('name', this.selectedItemHiddenInputNameValue).attr('type', 'hidden').attr('data-input', 'on').val(value);

    $input.appendTo($selectedRow);

    return $selectedRow;
  }

  findSearchResultRow(value: any): JQuery {
    return this.$searchResults.find('[data-row]')
      .filter('value'.asDataSelector(value))
  }

  findSearchResultsGrouping(groupValue: any): JQuery {
    return this.$searchResults.find('group'.asDataSelector(groupValue));
  }

  findSelectedResultsGrouping(groupValue: any): JQuery {
    return this.$selectedResults.find('group'.asDataSelector(groupValue));
  }

  insertRowIntoSelectedItems($row: JQuery): void {
    let selectedRowText = $row.data('sort').toLowerCase();
    let rows = this.$selectedResults.find('[data-row]').toArray();
    if (rows.length == 0)
      this.$selectedResults.append($row);
    else {
      let i = 0;
      while (!$row.parent().length) {
        let currentRowText = rows[i].getAttribute('data-sort').trim().toLowerCase();
        if (selectedRowText >= currentRowText && i == rows.length - 1)
          $row.insertAfter(rows[i]);
        else if (selectedRowText < currentRowText)
          $row.insertBefore(rows[i]);
        i++;
      }
    }
  }
}

function Plugin(option, args) {
  return this.each(function () {
    let $this = $(this);
    let data = $this.data('rustig.duallistbox');
    let options = typeof option === 'object' && option;

    if (!data) {
      data = new DualListBox($this, options);
      $this.data('rustig.duallistbox', data);
    }
    if (typeof (option) == 'string')
      data[option](args);
  })
}

declare global {
  interface JQuery {
    duallistbox: (option, args) => any;
  }
}

$.fn.duallistbox = Plugin;

export default DualListBox;